import classNames from 'classnames'
import styles from './ProgressBar.module.css'
import useDeltaColor from './useDeltaColor'
import { type CSSProperties } from 'react'

type ProgressBarWithPercentage = {
  progressPercentage: number
  value?: never
}

type ProgressBarWithValue = {
  progressPercentage?: never
  value: number
}
type ProgressBarProps = {
  max?: number
  progressBackgroundColor?: string | undefined
  progressColor?: string | undefined
  darkMode?: boolean | undefined
  intervalMs?: number
  min?: number
  invisibleLabelText?: string
  valueText?: string
  id?: string
  style?: CSSProperties
} & (ProgressBarWithPercentage | ProgressBarWithValue)

const INTERVAL_MS = 5000
const DEFAULT_MAX_VALUE = 100

function ProgressBar({
  id,
  progressBackgroundColor,
  progressColor,
  intervalMs = INTERVAL_MS,
  darkMode,
  value,
  max: max_ = DEFAULT_MAX_VALUE,
  min = 0,
  invisibleLabelText,
  valueText,
  progressPercentage: progressPercentage_,
}: ProgressBarProps) {
  const max = max_ || DEFAULT_MAX_VALUE
  const progressPercentage = progressPercentage_ ?? (value / max) * 100

  const { delta, deltaColor, backgroundColor, color } = useDeltaColor(
    progressPercentage,
    intervalMs,
    progressColor,
    progressBackgroundColor,
    darkMode
  )

  return (
    <div
      id={id}
      role="progressbar"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={valueText}
      aria-label={invisibleLabelText}
      // keeping the non-module class so it's possible to target the element from OBS
      className={classNames(styles.progressbar, 'progressbar')}
      style={
        {
          '--transition-duration': `${intervalMs}ms`,
          '--background-color': backgroundColor,
          '--color': color,
          '--percent-delta': delta / progressPercentage,
          '--delta-color': deltaColor,
        } as CSSProperties
      }
    >
      <span style={{ maxInlineSize: `${Math.round(progressPercentage)}%` }}></span>
    </div>
  )
}

export default ProgressBar
